import React, { useMemo } from "react";
import EditorPic from "../Proposal Section View/Editor.png";
import { ExpandCollapseIcon } from "../Proposal Section View/svgIcons";

const CommentTab = ({
  proposalData,
  filterType,
  expandedMemberIds,
  toggleMemberCard,
  getInitials,
  commentInputs,
  loadingId,
  successId,
  resolvingIds,
  resolveSuccessIds,
  canResolve,
  handleCommentChange,
  handleAddComment,
  handleResolveToggle,
}) => {
  
  const aggregatedComments = useMemo(() => {
    const sections = proposalData?.sections || proposalData?.analysis?.toc || proposalData?.proposal_content || [];

    let list = [];

    sections.forEach((section) => {
      const members = section.assigned_members || [];
      members.forEach((member) => {
        if (filterType !== "All" && member.role?.toLowerCase() !== filterType.toLowerCase()) {
          return;
        }

        let latestTs = 0;
        let sortedComments = [];

        if (member.comments && member.comments.length > 0) {
          sortedComments = [...member.comments].sort(
            (a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime()
          );
          latestTs = new Date(sortedComments[0].timestamp || 0).getTime();
        }

        const sectionId = section.id || section.section_id;
        // "comment-" prefix keeps these IDs distinct from the Review Status tab
        const uniqueId = `comment-${member.user_id}-${sectionId}`;

        list.push({
          ...member,
          comments: sortedComments,
          sectionNumber: section.number,
          sectionTitle: section.title,
          sectionId: sectionId,
          latestTs,
          uniqueId: uniqueId,
        });
      });
    });

    return list.sort((a, b) => b.latestTs - a.latestTs);
  }, [proposalData, filterType]);

  return (
    <div className="flex-1 overflow-y-auto px-3 py-6 pt-0 custom-scrollbar">
      {aggregatedComments.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="mb-6 relative">
            <img src={EditorPic} alt="No team members" className="w-48 h-auto object-contain" />
          </div>
          <p className="text-[#6B7280] font-outfit text-base font-medium text-center mb-1">
            No comments or team members found!
          </p>
        </div>
      )}

      {aggregatedComments.length > 0 && (
        <div className="space-y-4">
          {aggregatedComments.map((item, idx) => {
            const isExpanded = expandedMemberIds.has(item.uniqueId);
            const currentText = commentInputs[item.uniqueId] || "";
            const isTyping = currentText.trim().length > 0;

            const isAdding = loadingId === item.uniqueId;
            const isAddSuccess = successId === item.uniqueId;

            const isResolving = resolvingIds[item.uniqueId];
            const showResolvedSuccess = resolveSuccessIds[item.uniqueId];
            const isResolved = item.comment_resolved === true;

            return (
              <div key={item.uniqueId || idx} className="border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden">
                <div
                  className="p-4 flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleMemberCard(item.uniqueId)}
                >
                  <div className="w-10 h-10 rounded-full bg-[#C95EBE] flex items-center justify-center text-white font-outfit font-medium flex-shrink-0">
                    {getInitials(item.full_name || item.email)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-[#18101C] font-outfit text-[14px] font-medium truncate">
                        {item.full_name || item.email}
                      </p>
                      <span className="bg-[#FFF2FE] text-[#C95EBE] text-[10px] px-2 py-0.5 rounded-[8px] font-medium">
                        {item.role || ""}
                      </span>
                    </div>
                    <p className="text-[#3F3F46] font-outfit text-[12px]">
                      {item.comments ? item.comments.length : 0} Comments
                    </p>
                  </div>

                  <ExpandCollapseIcon className={`transform transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`} />
                </div>

                {isExpanded && (
                  <div className="px-4 pb-4 pt-0">
                    <div className="h-px w-full bg-gray-100 mb-4"></div>

                    <div className="mb-2">
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-[#3F3F46] font-outfit text-[14px] font-medium">
                          Section Assigned
                        </label>

                        {canResolve && (
                          <button
                            onClick={() => handleResolveToggle(item.user_id, isResolved, item.sectionId, item.uniqueId)}
                            disabled={isResolving || showResolvedSuccess}
                            className={`text-xs font-medium hover:underline transition-colors ${
                              isResolved ? "text-[#6B7280]" : "text-[#10B981]"
                            }`}
                          >
                            {isResolving ? "Resolving..." : showResolvedSuccess ? "Resolved" : isResolved ? "Undo" : "Resolve Comment"}
                          </button>
                        )}
                      </div>
                      <div className="w-full bg-[#F9FAFB] border border-gray-200 rounded px-3 py-2 text-[#6B7280] font-outfit text-sm">
                        {item.sectionNumber}. {item.sectionTitle}
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="text-[#3F3F46] font-outfit text-[12px] font-medium mb-2 block">
                        Comments ({item.comments ? item.comments.length : 0})
                      </label>

                      {item.comments && item.comments.length > 0 ? (
                        <div className="space-y-3 mb-3">
                          {item.comments.map((comment, cIdx) => (
                            <div key={cIdx} className="bg-gray-50 p-2 rounded">
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-[#C95EBE] text-xs font-medium">
                                  {comment.author_name || item.full_name}
                                  </span>
                                <span className="text-[#9CA3AF] text-[10px]">
                                  {comment.timestamp
                                    ? new Date(comment.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                    : "Just now"}
                                </span>
                              </div>
                              <p className="text-[#4B5563] text-sm">{comment.text || "No text"}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[#9CA3AF] text-xs italic mb-3">No comments yet.</p>
                      )}
                    </div>

                    {!isResolved && (
                      <div className="space-y-2">
                        <textarea
                          className="w-full border border-gray-200 rounded-lg p-2 text-sm font-outfit focus:ring-1 focus:ring-[#C95EBE] focus:border-[#C95EBE] outline-none resize-none"
                          placeholder="Write your comment..."
                          rows="2"
                          value={currentText}
                          onChange={(e) => handleCommentChange(item.uniqueId, e.target.value)}
                        ></textarea>
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => handleCommentChange(item.uniqueId, "")}
                            className="px-3 py-1.5 rounded border border-[#C95EBE] text-[#C95EBE] text-xs font-medium hover:bg-pink-50 transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleAddComment(item.uniqueId, item.sectionId, item.user_id)}
                            disabled={!isTyping || isAdding || isAddSuccess}
                            className={`px-3 py-1.5 rounded text-white text-xs font-medium transition-all shadow-sm ${
                              !isTyping || isAdding || isAddSuccess ? "bg-[#C95EBE] opacity-50 cursor-not-allowed" : "bg-[#C95EBE] hover:bg-[#BB42AE]"
                            }`}
                          >
                            {isAdding ? "Adding..." : isAddSuccess ? "Added" : "Add Comment"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CommentTab;
